/* Types */
import { CellContext, Row } from '@tanstack/react-table';
import { ClientLink } from 'application/Shared/types/clientLink';
import {
    ProviderType,
    TenantProvider
} from 'application/Shared/types/tenantProvider';

/*  React  */
import React from 'react';

import { IntegrationStatus } from '../Avatars/IntegrationStatus';
import { ColumnFilter } from '../Table/types';

/**
 * Configuration object for integration status filtering control.
 */
export const integrationStatusFilter: ColumnFilter<unknown> = {
    control: 'Checkbox',
    defaultOptions: [
        {
            group: 'Status',
            options: [
                'PartnerOrganization.integrationStatus.connected',
                'PartnerOrganization.integrationStatus.disconnected',
                'PartnerOrganization.integrationStatus.updateRequired',
                'PartnerOrganization.integrationStatus.noIntegration'
            ]
        },
        {
            group: 'Provider',
            options: [
                'Common.google_workspace',
                'Common.microsoft',
                'Common.okta'
            ]
        }
    ]
};

/**
 * Filters data based on provider.connectionDetails and provider types.
 *
 * @template TData - The type of data being filtered
 * @param {Row<TData>} row - The row of data to check
 * @param {string} columnId - The ID of the column containing the date
 * @param {string} filterValue - The filter value (e.g., 'Authenticated', 'Disconnected', 'Google Workspace', 'Microsoft', 'Okta')
 *
 * @returns {boolean} - True if the provider.connectionDetails meets a criteria, false otherwise
 *
 * @example
 * // Returns true if the provider.connectionDetails in the row is null or undefined
 * integrationStatusFilterFn(row, 'provider.connectionDetails', 'Not integrated')
 */
export function integrationStatusFilterFn<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string
): boolean {
    if (!filterValue) return true;

    const filterValueArray = Array.isArray(filterValue)
        ? filterValue
        : [filterValue];

    if (filterValueArray.length === 0) return true;

    const tenantProviders = row.getValue(columnId) as TenantProvider[];

    // Define filter mappings based on the group structure
    const STATUS_FILTERS = [
        'PartnerOrganization.integrationStatus.connected',
        'PartnerOrganization.integrationStatus.disconnected',
        'PartnerOrganization.integrationStatus.updateRequired',
        'PartnerOrganization.integrationStatus.noIntegration'
    ];

    const PROVIDER_FILTERS = [
        'Common.google_workspace',
        'Common.microsoft',
        'Common.okta'
    ];

    // Separate filters into status and provider filters
    const statusFilters = filterValueArray.filter((value) =>
        STATUS_FILTERS.includes(value)
    );
    const providerFilters = filterValueArray.filter((value) =>
        PROVIDER_FILTERS.includes(value)
    );

    // If no status filters are selected, we only need to check provider filters
    const shouldCheckStatus = statusFilters.length > 0;
    const shouldCheckProvider = providerFilters.length > 0;

    // If neither status nor provider filters are selected, show all rows
    if (!shouldCheckStatus && !shouldCheckProvider) {
        return true;
    }

    // Get all providers (Microsoft, Google, Okta)
    const providers =
        tenantProviders?.filter(
            (provider) =>
                provider.providerType === ProviderType.Microsoft ||
                provider.providerType === ProviderType.Google ||
                provider.providerType === ProviderType.Okta
        ) || [];

    // Check provider filter first
    if (shouldCheckProvider) {
        const hasMatchingProvider = providers.some((provider) => {
            switch (provider.providerType) {
                case ProviderType.Google:
                    return providerFilters.includes('Common.google_workspace');
                case ProviderType.Microsoft:
                    return providerFilters.includes('Common.microsoft');
                case ProviderType.Okta:
                    return providerFilters.includes('Common.okta');
                default:
                    return false;
            }
        });

        // If no matching providers found, exclude this row
        if (!hasMatchingProvider) {
            return false;
        }
    }

    // If only provider filtering is needed, return true (we already checked providers above)
    if (!shouldCheckStatus) {
        return true;
    }

    // Check status filter
    // If no relevant providers exist, return no integration status
    if (providers.length === 0) {
        return statusFilters.includes(
            'PartnerOrganization.integrationStatus.noIntegration'
        );
    }

    // Check if any provider requires updates
    const hasUpdateRequired = providers.some(
        (provider) => provider.connectionDetails?.isUpdateRequired
    );
    if (hasUpdateRequired) {
        return statusFilters.includes(
            'PartnerOrganization.integrationStatus.updateRequired'
        );
    }

    // Check if any provider is enabled (connected)
    const hasConnected = providers.some((provider) => provider.enabled);
    if (hasConnected) {
        return statusFilters.includes(
            'PartnerOrganization.integrationStatus.connected'
        );
    }

    // If providers exist but none are enabled, status is disconnected
    return statusFilters.includes(
        'PartnerOrganization.integrationStatus.disconnected'
    );
}

/**
 * Helper function that determines the integration status for each provider in the array.
 * Iterates over all providers and checks their individual status:
 * 1. If provider is null/undefined, there is no integration
 * 2. If update is required (connectionDetails.isUpdateRequired is true), there is an integration update required
 * 3. If provider is enabled, there is an integration authenticated/connected
 * 4. If provider exists but is not enabled, there was an integration added but later disconnected
 *
 * @param tenantProviders - Array of tenant providers to check
 * @param t - Translation function for localizing status messages
 * @returns {Array<{providerType: string, status: string}>} - Array of provider types and their integration status
 */
export const getIntegrationStatus = (
    tenantProviders: TenantProvider[],
    t: (key: string) => string
): Array<{ provider: string; integrationStatus: string }> => {
    const providerTypes = Object.values(ProviderType);

    return providerTypes.map((type) => {
        const provider = tenantProviders.find((p) => p.providerType === type);

        let status;

        if (!provider) {
            status = t('PartnerOrganization.integrationStatus.noIntegration');
        } else if (provider.connectionDetails?.isUpdateRequired) {
            status = t('PartnerOrganization.integrationStatus.updateRequired');
        } else if (provider.enabled) {
            status = t('PartnerOrganization.integrationStatus.connected');
        } else {
            status = t('PartnerOrganization.integrationStatus.disconnected');
        }

        return {
            integrationStatus: status,
            provider: type
        };
    });
};

/**
 * React component that renders the integration status for a partner organization.
 * @param cell - The cell context containing the connection details
 * @returns {string | null} - The localized integration status string or null
 */
export const IntegrationStatusCell = ({
    row
}: CellContext<ClientLink, TenantProvider[]>): string | React.ReactNode => {
    const { provider: tenantProviders = [], tenant } = row.original;

    return <IntegrationStatus tenant={tenant!} providers={tenantProviders} />;
};
